import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import Loader from '../../components/Common/Loader';
import Collapse from '../../components/Common/Collapse';
import { getSections } from '../../services/studentService';
import { FileIcon, fileColor, fileMeta } from '../../components/Common/fileKind';

const MonthRu = ['ЯНВ','ФЕВ','МАР','АПР','МАЙ','ИЮН','ИЮЛ','АВГ','СЕН','ОКТ','НОЯ','ДЕК'];

const IcSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
// Иконка модуля/раздела = иконка раздела «Материалы» из сайдбара (файл)
const IcSection = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
  </svg>
);
const IcChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

function LessonGroup({ lesson, materials, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const d = lesson.date ? new Date(lesson.date) : null;
  return (
    <div className={`mod${open ? ' open' : ''}`}>
      <div className="mod-head" onClick={() => setOpen(o => !o)}>
        <div className="mod-ic"><IcSection /></div>
        <div className="mod-info">
          <div className="mod-title">{lesson.title}</div>
          <div className="mod-sub">{d ? `${d.getDate()} ${MonthRu[d.getMonth()]}` : 'без даты'}</div>
        </div>
        <span className="mod-count">{materials.length}</span>
        <span className="mod-chev"><IcChevron /></span>
      </div>
      <Collapse open={open}>
        <div className="mod-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 14 }}>
            {materials.map((mat, idx) => {
              const name = mat.filename || mat.name || mat.title || 'Файл';
              // Локальные файлы (/uploads) качаем под настоящим именем через ?name;
              // на проде имя задаёт S3.
              const rawUrl = mat.url || mat.link || '#';
              const href = rawUrl.includes('/uploads/')
                ? `${rawUrl}?name=${encodeURIComponent(name)}`
                : rawUrl;
              return (
                <a key={idx} href={href} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 15, padding: '14px 16px', textDecoration: 'none', transition: 'border-color .15s, box-shadow .15s, transform .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(124,77,255,0.4)'; e.currentTarget.style.boxShadow = '0 12px 28px -18px rgba(124,77,255,0.5)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                  <div style={{ width: 46, height: 46, borderRadius: 13, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: fileColor(name)[0], color: fileColor(name)[1] }}><FileIcon name={name} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{fileMeta(mat, name)}</div>
                  </div>
                  <span className="mat-dl-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 11, background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 12.5, fontWeight: 700, flexShrink: 0 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Скачать
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </Collapse>
    </div>
  );
}

const MaterialsPage = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const { selectedCourseId } = useOutletContext() || {};

  useEffect(() => {
    getSections(selectedCourseId)
      .then(res => setSections(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedCourseId]);

  const sectionGroups = sections
    .map(section => {
      const lessons = (section.Lessons || [])
        .map(lesson => {
          const mats = Array.isArray(lesson.materials) ? lesson.materials : [];
          const filtered = search
            ? mats.filter(m => {
                const name = (m.filename || m.name || m.title || '').toLowerCase();
                return name.includes(search.toLowerCase()) || lesson.title.toLowerCase().includes(search.toLowerCase());
              })
            : mats;
          return { lesson, materials: filtered };
        })
        .filter(l => l.materials.length > 0);
      return { section, lessons };
    })
    .filter(sg => sg.lessons.length > 0);

  const totalMaterials = sections.reduce((acc, s) =>
    acc + (s.Lessons || []).reduce((a, l) => a + (l.materials?.length || 0), 0), 0
  );

  if (loading) return <div className="s-page"><Loader /></div>;

  return (
    <div className="s-page">
      <div className="s-page-header">
        <div>
          <h1 className="s-page-title">Материалы</h1>
          <p className="s-page-sub">Файлы и ресурсы по урокам курса</p>
        </div>
      </div>

      <div className="pg-search">
        <IcSearch />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск по названию файла или уроку..." />
      </div>

      {totalMaterials === 0 ? (
        <div className="pg-empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 10 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
          <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>Материалы пока не добавлены</div>
          Файлы появятся, когда преподаватель добавит их к урокам
        </div>
      ) : sectionGroups.length === 0 ? (
        <div className="pg-empty">Ничего не найдено</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
          {sectionGroups.map(({ section, lessons }) => (
            <div key={section.id}>
              <div className="mat-sec-head">
                <IcSection />
                <span>{section.name}</span>
                <div className="ln" />
              </div>
              {lessons.map(({ lesson, materials }) => (
                <LessonGroup key={lesson.id} lesson={lesson} materials={materials} defaultOpen={lessons.length === 1} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MaterialsPage;
